import { clientEnv } from '../config/env';
import { appendWalletClientQuery } from './walletClient';

function trimTrailingSlash(url) {
  return String(url || '').replace(/\/+$/, '');
}

const WALLET_PAY_PATH_RE = /\/walletPay\/tgNo\/[^/]+\/referralCode\/[^/?#]+/i;
const WALLET_LINK_PATH_RE = /\/walletLink\/referralCode\/[^/?#]+/i;
const GID_SESSION_KEY = 'shop_game_group_id';
const CODE_SESSION_KEY = 'shop_referral_code';
const PROMO_CACHE_KEY = 'shop_promo_cache';
/** 与 shop-bot / Telegram start 统一：推广码_g_群ID[_k_linkKey] */
const REFERRAL_GID_SUFFIX = '_g_';
const REFERRAL_LINK_KEY_SUFFIX = '_k_';

/** 钱包 DApp 深链接目标站点（来自 VUE_APP_DEEPLINK_BASE_URL） */
export function getDeeplinkBaseUrl() {
  return trimTrailingSlash(clientEnv.deeplinkBaseUrl);
}

/**
 * 解析单参数推广载荷。
 * 例：Abc / Abc_g_-100123 / Abc_g_-100123_k_pickup1
 * 先剥 _k_linkKey，再剥 _g_gid，避免 gid 校验被污染。
 */
export function parseReferralPayload(raw) {
  let payload = String(raw || '').trim();
  if (!payload) return { referralCode: '', gameGroupId: '', linkKey: '' };

  let linkKey = '';
  const kIdx = payload.lastIndexOf(REFERRAL_LINK_KEY_SUFFIX);
  if (kIdx > 0) {
    const maybeKey = payload.slice(kIdx + REFERRAL_LINK_KEY_SUFFIX.length);
    if (maybeKey && /^[A-Za-z0-9._-]+$/.test(maybeKey)) {
      linkKey = maybeKey;
      payload = payload.slice(0, kIdx);
    }
  }

  const gIdx = payload.lastIndexOf(REFERRAL_GID_SUFFIX);
  if (gIdx > 0) {
    const referralCode = payload.slice(0, gIdx);
    const gameGroupId = payload.slice(gIdx + REFERRAL_GID_SUFFIX.length);
    if (referralCode && /^-?\d+$/.test(gameGroupId) && Number(gameGroupId) !== 0) {
      return { referralCode, gameGroupId, linkKey };
    }
  }
  return { referralCode: payload, gameGroupId: '', linkKey };
}

/**
 * 合并推广码 / gid / link_key 为单参数。
 * 例：Abc_g_-100123_k_pickup1
 */
export function buildReferralPayload(referralCode, gameGroupId, linkKey) {
  const code = String(referralCode || '').trim();
  if (!code) return '';
  const parsed = parseReferralPayload(code);
  const baseCode = parsed.referralCode || code;
  const gidRaw =
    gameGroupId != null && String(gameGroupId).trim() !== ''
      ? String(gameGroupId).trim()
      : parsed.gameGroupId;
  const keyRaw =
    linkKey != null && String(linkKey).trim() !== ''
      ? String(linkKey).trim()
      : parsed.linkKey;

  let out = baseCode;
  if (gidRaw && /^-?\d+$/.test(gidRaw) && Number(gidRaw) !== 0) {
    out += `${REFERRAL_GID_SUFFIX}${gidRaw}`;
  }
  if (keyRaw && /^[A-Za-z0-9._-]+$/.test(keyRaw)) {
    out += `${REFERRAL_LINK_KEY_SUFFIX}${keyRaw}`;
  }
  return out;
}

/** 解析 gid 原始值（支持 Telegram 负数群 ID） */
export function parseGameGroupIdValue(raw) {
  if (raw == null || raw === '') return undefined;
  const s = decodeURIComponent(String(raw)).trim();
  const n = Number(s);
  if (Number.isFinite(n) && n !== 0) return n;
  return s || undefined;
}

export function persistGameGroupId(gameGroupId) {
  if (gameGroupId == null || gameGroupId === '') return;
  try {
    sessionStorage.setItem(GID_SESSION_KEY, String(gameGroupId));
  } catch {
    /* ignore */
  }
}

export function readPersistedGameGroupId() {
  try {
    return parseGameGroupIdValue(sessionStorage.getItem(GID_SESSION_KEY));
  } catch {
    return undefined;
  }
}

export function persistReferralCode(referralCode) {
  if (referralCode == null || referralCode === '') return;
  try {
    sessionStorage.setItem(CODE_SESSION_KEY, String(referralCode));
  } catch {
    /* ignore */
  }
}

export function readPersistedReferralCode() {
  try {
    return sessionStorage.getItem(CODE_SESSION_KEY) || '';
  } catch {
    return '';
  }
}

/** 短码 p 解析结果（同一会话内复用，避免每页再请求） */
export function persistPromoResolution(shortCode, data) {
  if (!shortCode || !data) return;
  const payload = {
    p: String(shortCode),
    referralCode: String(data.referralCode || ''),
    gameGroupId: String(data.gameGroupId || ''),
    linkKey: String(data.linkKey || ''),
  };
  try {
    sessionStorage.setItem(PROMO_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
  if (payload.referralCode) persistReferralCode(payload.referralCode);
  if (payload.gameGroupId) persistGameGroupId(payload.gameGroupId);
}

export function readPromoCache(shortCode) {
  if (!shortCode) return null;
  try {
    const raw = sessionStorage.getItem(PROMO_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || String(data.p) !== String(shortCode)) return null;
    return data;
  } catch {
    return null;
  }
}

function promoFromSearchParams(params) {
  const p = params.get('p') || '';
  if (!p) return null;
  return readPromoCache(p);
}

/** 从 hash 片段读取 gid（部分钱包保留 hash） */
function readGameGroupIdFromHash() {
  if (typeof window === 'undefined') return undefined;
  const hash = window.location.hash || '';
  const m = hash.match(/(?:^|[#&?])gid=([^&]+)/);
  return m ? parseGameGroupIdValue(m[1]) : undefined;
}

/**
 * 在 URL 上追加 gid。
 * walletPay / walletLink 使用路径段 /gid/{id}，避免 DApp 内嵌浏览器剥离 ?query。
 * 其它 URL：若已有 code/referral_code，合并为单参数 code_g_gid。
 */
export function appendGameGroupId(url, gameGroupId) {
  if (!gameGroupId) return url;
  const gid = String(gameGroupId);
  const hashIdx = url.indexOf('#');
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : '';
  const withoutHash = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
  const qIdx = withoutHash.indexOf('?');
  const pathPart = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
  const query = qIdx >= 0 ? withoutHash.slice(qIdx + 1) : '';

  if (WALLET_PAY_PATH_RE.test(pathPart) && !/\/gid\/[^/]+$/i.test(pathPart)) {
    const next = `${pathPart}/gid/${encodeURIComponent(gid)}`;
    return (query ? `${next}?${query}` : next) + hash;
  }
  if (WALLET_LINK_PATH_RE.test(pathPart) && !/\/gid\/[^/]+$/i.test(pathPart)) {
    const next = `${pathPart}/gid/${encodeURIComponent(gid)}`;
    return (query ? `${next}?${query}` : next) + hash;
  }

  const params = new URLSearchParams(query);
  const existing =
    params.get('code') || params.get('referral_code') || params.get('r') || '';
  if (existing) {
    const { referralCode, linkKey } = parseReferralPayload(existing);
    const legacyKey = params.get('link_key') || '';
    const merged = buildReferralPayload(
      referralCode || existing,
      gid,
      linkKey || legacyKey,
    );
    if (params.has('code')) params.set('code', merged);
    else if (params.has('referral_code')) params.set('referral_code', merged);
    else if (params.has('r')) params.set('r', merged);
    params.delete('gid');
    params.delete('link_key');
    const qs = params.toString();
    return (qs ? `${pathPart}?${qs}` : pathPart) + hash;
  }

  const sep = query ? '&' : '?';
  const glued = query
    ? `${pathPart}?${query}${sep}gid=${encodeURIComponent(gid)}`
    : `${pathPart}?gid=${encodeURIComponent(gid)}`;
  return glued + hash;
}

/**
 * 从 URLSearchParams 读取推广上下文。
 * 优先单参数 code / referral_code / r（可含 _g_gid / _k_linkKey）；
 * 兼容旧链 ?code=&gid=&link_key=。
 */
export function readReferralFromSearch(search) {
  const params =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(typeof search === 'string' ? search : window.location.search);

  const combinedRaw =
    params.get('code') || params.get('referral_code') || params.get('r') || '';
  const parsed = parseReferralPayload(combinedRaw);
  const legacyGid = params.get('gid') || '';
  const legacyLinkKey = params.get('link_key') || '';
  const fromPromo = promoFromSearchParams(params);

  return {
    referralCode: fromPromo?.referralCode || parsed.referralCode,
    gameGroupId: fromPromo?.gameGroupId || parsed.gameGroupId || legacyGid || '',
    linkKey: fromPromo?.linkKey || parsed.linkKey || legacyLinkKey || '',
  };
}

/** 从路由 path / query / 浏览器 URL / session 读取 gid */
export function getGameGroupIdFromRoute(route) {
  const pRaw = route?.query?.p;
  const p = Array.isArray(pRaw) ? pRaw[0] : pRaw;
  const fromPromo = p ? readPromoCache(String(p))?.gameGroupId : '';
  const fromParams = route?.params?.gid ?? route?.params?.gameGroupId;
  const fromQueryCombined = (() => {
    const q = route?.query || {};
    const raw = q.code || q.referral_code || q.r;
    if (!raw) return '';
    return parseReferralPayload(Array.isArray(raw) ? raw[0] : raw).gameGroupId;
  })();
  const fromQuery = route?.query?.gid;
  const fromSearch =
    typeof window !== 'undefined'
      ? readReferralFromSearch(window.location.search).gameGroupId
      : '';
  const fromHash = readGameGroupIdFromHash();
  const fromSession = readPersistedGameGroupId();

  const raw = [fromParams, fromPromo, fromQueryCombined, fromQuery, fromSearch, fromHash, fromSession]
    .find((v) => v != null && String(v).trim() !== '');
  const parsed = parseGameGroupIdValue(raw);
  if (parsed != null && parsed !== '') {
    persistGameGroupId(parsed);
  }
  return parsed;
}

/** 从 payload / 路由 / URL 解析 gid */
export function resolveGameGroupId(source = {}) {
  const combinedRaw =
    source.code ??
    source.referral_code ??
    source.r ??
    source?.query?.code ??
    source?.query?.referral_code ??
    source?.query?.r;
  const fromCombined = combinedRaw
    ? parseReferralPayload(
        Array.isArray(combinedRaw) ? combinedRaw[0] : combinedRaw,
      ).gameGroupId
    : '';
  const candidates = [
    source.gameGroupId,
    source.gid,
    source.game_group_id,
    fromCombined,
    source?.params?.gid,
    source?.query?.gid,
    typeof window !== 'undefined'
      ? readReferralFromSearch(window.location.search).gameGroupId
      : '',
  ];
  const raw = candidates.find((v) => v != null && String(v).trim() !== '');
  const parsed = parseGameGroupIdValue(raw);
  if (parsed != null && parsed !== '') {
    persistGameGroupId(parsed);
  }
  return parsed;
}

/** 统一解析推广上下文：code + gid + linkKey（支持单参数与旧多参数） */
export function resolveReferralContext(route) {
  const fromSearch = readReferralFromSearch(window.location.search);
  const query = route?.query || {};
  const pRaw = query.p;
  const p = Array.isArray(pRaw) ? pRaw[0] : pRaw;
  const fromPromo = p ? readPromoCache(String(p)) : null;

  const queryCodeRaw = query.code || query.referral_code || query.r || '';
  const queryCode = Array.isArray(queryCodeRaw) ? queryCodeRaw[0] : queryCodeRaw;
  const fromQueryPayload = parseReferralPayload(queryCode || '');

  const gidCandidates = [
    route?.params?.gid,
    fromPromo?.gameGroupId,
    fromQueryPayload.gameGroupId,
    query.gid,
    fromSearch.gameGroupId,
    readPersistedGameGroupId(),
  ];
  const gidRaw = gidCandidates.find((v) => v != null && String(v).trim() !== '');

  let gameGroupId = '';
  if (gidRaw != null && gidRaw !== '') {
    const parsed = parseGameGroupIdValue(gidRaw);
    gameGroupId = parsed != null ? String(parsed) : String(gidRaw);
    persistGameGroupId(gameGroupId);
  }

  const pathCode = route?.params?.referralCode;
  let referralCode = '';
  if (pathCode) {
    referralCode = parseReferralPayload(String(pathCode)).referralCode || String(pathCode);
  } else {
    referralCode =
      fromPromo?.referralCode ||
      fromQueryPayload.referralCode ||
      fromSearch.referralCode ||
      readPersistedReferralCode() ||
      '';
  }
  if (referralCode) persistReferralCode(referralCode);

  const legacyLinkKeyRaw = query.link_key;
  const legacyLinkKey = Array.isArray(legacyLinkKeyRaw)
    ? legacyLinkKeyRaw[0]
    : legacyLinkKeyRaw;
  const linkKey =
    fromPromo?.linkKey ||
    fromQueryPayload.linkKey ||
    fromSearch.linkKey ||
    (legacyLinkKey != null ? String(legacyLinkKey).trim() : '') ||
    '';

  return {
    referralCode,
    gameGroupId,
    linkKey,
  };
}

/**
 * 构建带推广信息的路由 query（单参数 code=码_g_gid[_k_linkKey]）。
 * extra.link_key 会并入 code，不再单独写出。
 */
export function buildReferralQuery(referralCode, gameGroupId, extra = {}) {
  const q = { ...extra };
  const linkKey = q.link_key != null ? String(q.link_key) : '';
  delete q.link_key;
  delete q.gid;
  const payload = buildReferralPayload(referralCode, gameGroupId, linkKey);
  if (payload) q.code = payload;
  return q;
}

/** 订单/授权 payload 附加 gid（兼容多种字段名） */
export function attachGameGroupId(payload, gameGroupId) {
  if (!payload || !gameGroupId) return payload;
  return {
    ...payload,
    gameGroupId,
    gid: gameGroupId,
    game_group_id: gameGroupId,
  };
}

/** Vue Router 跳转 WalletPay 时保留 gid（走路径段） */
export function buildWalletPayRoute(tgNo, referralCode, gameGroupId) {
  const { referralCode: code, gameGroupId: fromPayload } = parseReferralPayload(referralCode);
  const cleanCode = code || String(referralCode || '');
  const gid = gameGroupId || fromPayload;
  let path = `/walletPay/tgNo/${tgNo}/referralCode/${cleanCode}`;
  if (gid) {
    path += `/gid/${encodeURIComponent(String(gid))}`;
  }
  return { path };
}

/** Vue Router 跳转 WalletLink 时保留 gid（走路径段） */
export function buildWalletLinkRoute(referralCode, gameGroupId) {
  const { referralCode: code, gameGroupId: fromPayload } = parseReferralPayload(referralCode);
  const cleanCode = code || String(referralCode || '');
  const gid = gameGroupId || fromPayload;
  let path = `/walletLink/referralCode/${cleanCode}`;
  if (gid) {
    path += `/gid/${encodeURIComponent(String(gid))}`;
  }
  return { path };
}

/**
 * 构建 /walletPay/tgNo/{no}/referralCode/{code} 完整 URL
 * gid 放在路径 /gid/{id}，兼容 ?gid= 旧链接
 */
export function buildWalletPayUrl(tgNo, referralCode, { encoded = false, gameGroupId, wallet } = {}) {
  const base = getDeeplinkBaseUrl();
  const parsed = parseReferralPayload(referralCode);
  const cleanCode = parsed.referralCode || String(referralCode ?? '');
  const gid = gameGroupId || parsed.gameGroupId;
  const no = encoded ? encodeURIComponent(tgNo ?? '') : String(tgNo ?? '');
  const code = encoded ? encodeURIComponent(cleanCode) : cleanCode;
  let url = `${base}/walletPay/tgNo/${no}/referralCode/${code}`;
  if (gid) {
    url += `/gid/${encodeURIComponent(String(gid))}`;
  }
  return appendWalletClientQuery(url, wallet);
}

/** 构建 walletLink 完整 URL */
export function buildWalletLinkUrl(referralCode, gameGroupId, wallet) {
  const base = getDeeplinkBaseUrl();
  const parsed = parseReferralPayload(referralCode);
  const cleanCode = parsed.referralCode || String(referralCode ?? '');
  const gid = gameGroupId || parsed.gameGroupId;
  let url = `${base}/walletLink/referralCode/${encodeURIComponent(cleanCode)}`;
  if (gid) {
    url += `/gid/${encodeURIComponent(String(gid))}`;
  }
  return appendWalletClientQuery(url, wallet);
}

/**
 * 构建与 Payment / TgPayment 一致的钱包 App 深链接。
 * DApp 目标为 /walletPay/tgNo/{orderNo}/…（路径参数名历史叫 tgNo，实为订单号）。
 * imToken / Bitget 必须 encodeURIComponent(dappUrl)，否则 ?wallet= 会被外层 URL 截断。
 */
export function buildWalletPayDeeplink(wallet, orderNo, referralCode, gameGroupId) {
  const w = String(wallet || '').trim().toLowerCase();
  const payOpts = { gameGroupId, wallet: w };
  const dappUrl = buildWalletPayUrl(
    orderNo,
    referralCode,
    w === 'tokenpocket' ? { ...payOpts, encoded: true } : payOpts,
  );

  if (w === 'imtoken') {
    return `imtokenv2://navigate/DappView?url=${encodeURIComponent(dappUrl)}`;
  }
  if (w === 'tokenpocket') {
    const encodedParam = encodeURIComponent(
      JSON.stringify({ url: dappUrl, chain: 'Tron', source: 'shopSer' }),
    );
    return `tpdapp://open?params=${encodedParam}`;
  }
  if (w === 'bitget') {
    return `https://bkcode.vip?action=dapp&url=${encodeURIComponent(dappUrl)}`;
  }
  if (w === 'tronlink') {
    const encodedParam = encodeURIComponent(
      JSON.stringify({
        url: dappUrl,
        action: 'open',
        protocol: 'tronlink',
        version: '1.0',
      }),
    );
    return `tronlinkoutside://pull.activity?param=${encodedParam}`;
  }
  return dappUrl;
}

/** 跳转到钱包深链接（未知钱包则回退打开 DApp HTTPS） */
export function openWalletPayDeeplink(wallet, orderNo, referralCode, gameGroupId) {
  const href = buildWalletPayDeeplink(wallet, orderNo, referralCode, gameGroupId);
  if (typeof window !== 'undefined' && href) {
    window.location.href = href;
  }
  return href;
}
